import { useState } from "react";
import { router, usePage } from "@inertiajs/react";
import logoFull from "@/assets/brand/logo-full-script.webp";
import globe from "@/assets/brand/globe.png";
import { Input } from "@/components/ui/Field";
import Button, { ButtonAnchor } from "@/components/ui/Button";
import type { PublicProps } from "@/types";

import table from "@/assets/photos/event-table.webp";
import cafe from "@/assets/photos/event-cafe.webp";
import drinks from "@/assets/photos/event-drinks.webp";
import venue from "@/assets/photos/event-venue.webp";

// Photos of Hack Clubbers round a table with drinks, not the travel and
// landscape ones - this panel exists to answer "what would it look like if I
// turned up". Four, because this grid is 2x2 with taller cells than the city
// page's.
const PHOTOS = [table, cafe, drinks, venue];

// "2026-09-20" -> "09.20.2026". Split rather than parsed: `new Date` on a bare
// date string lands on UTC midnight and slides a day backwards west of London.
function stampDate(iso: string | null | undefined) {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return null;
  return `${m}.${d}.${y}`;
}

// The Figma hero: a translucent content panel over a pale sky, with a
// window-chrome panel of event photos beside it. Panels here use 1px
// #274e63 borders and translucent fills, which is why they're written out rather
// than reusing ui/Panel (2px brown, opaque cream).
export default function LandingHero({ cityCount }: { cityCount: number }) {
  const { props } = usePage<PublicProps>();
  const program = props.program;
  const [email, setEmail] = useState("");
  // Closing the organizer boards is how HQ ends a season, so the front door
  // follows it: no signups to offer, only a list for next time.
  const seasonOver = !program.organizer_boards_open;

  const start = stampDate(program.starts_on);
  const end = stampDate(program.ends_on);
  let dateLabel = start;
  if (start && end && start !== end) {
    // Same year: drop the repeat so the meta line stays on one line.
    const sameYear = start.slice(-4) === end.slice(-4);
    dateLabel = `${sameYear ? start.slice(0, 5) : start} to ${end}`;
  }

  // The Figma's meta line, minus anything we can't honestly fill in yet.
  const meta = [
    "3-5 hours",
    cityCount > 0 ? `${cityCount}+ cities` : "cities worldwide",
    "for teens 13-18",
    seasonOver ? null : dateLabel,
  ].filter(Boolean);

  return (
    <section className="border-blue-dianne/20 relative overflow-hidden border-b-2">
      {/* Pale sky, faint grid, and a globe wash - the Figma background stack. */}
      <div aria-hidden className="bg-band-hero absolute inset-0" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(39,78,99,.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(39,78,99,.07) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />
      <img
        src={globe}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-56 w-[52rem] max-w-none opacity-10"
      />

      <div className="relative mx-auto grid w-full max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:py-16">
        {/* Left: the pitch */}
        <div className="border-blue-dianne/60 shadow-card flex flex-col gap-5 border bg-[rgba(255,253,246,0.82)] p-6 backdrop-blur-sm sm:p-8">
          <img
            src={logoFull}
            alt={program.name}
            className="w-full max-w-sm self-start"
          />

          {/* The Figma underlines the final clause. Split on the last comma so
              the emphasis follows whatever the tagline actually says, instead of
              hardcoding (and duplicating) "get free boba!". */}
          <p className="font-display text-heading text-link font-bold">
            {(() => {
              const tagline =
                program.tagline ??
                "Make a website, hang out with friends, get free boba!";
              const split = tagline.lastIndexOf(", ");
              if (split === -1) return tagline;
              return (
                <>
                  {tagline.slice(0, split + 2)}
                  <span className="underline decoration-2">
                    {tagline.slice(split + 2)}
                  </span>
                </>
              );
            })()}
          </p>

          <p className="text-body text-ink-muted">{meta.join(" • ")}</p>

          <hr className="border-blue-dianne/30" />

          {seasonOver ? (
            <>
              <p className="font-display text-subheading text-ink font-extrabold italic">
                That's a wrap for this year! Want to know when it's back?
              </p>

              {/* A waiting list, not a signup: there is nothing open to sign
                  up for until the next season starts. */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  router.post("/rsvp", { email });
                }}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <label htmlFor="landing-email" className="sr-only">
                  Your email address
                </label>
                <Input
                  id="landing-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                  className="flex-1"
                />
                <Button type="submit" className="shrink-0">
                  tell me!
                </Button>
              </form>

              <p className="text-caption text-ink-soft">
                We'll only email you about the next Boba Bash.
              </p>
            </>
          ) : (
            <>
              <p className="font-display text-subheading text-ink font-extrabold italic">
                SIGN UP for a Boba Bash near you:
              </p>

              {/* The Figma's email capture. It hands the address straight to the
                signup form rather than to a mailing list, so the only thing left
                to do there is pick a city. */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  router.get("/rsvp", { email });
                }}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <label htmlFor="landing-email" className="sr-only">
                  Your email address
                </label>
                <Input
                  id="landing-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                  className="flex-1"
                />
                <Button type="submit" className="shrink-0">
                  sign up!
                </Button>
              </form>

              <p className="text-caption text-ink-soft">
                You'll pick your city on the next screen. Takes about a minute,
                and you don't need an account.
              </p>
            </>
          )}

          <div className="flex flex-wrap gap-3">
            <ButtonAnchor href="/events" variant="brand" size="lg">
              {seasonOver ? "see where it happened" : "find an event"}
            </ButtonAnchor>
          </div>
        </div>

        {/* Right: "what your event could look like" window */}
        <div className="border-blue-dianne/60 shadow-card flex flex-col border bg-[rgba(255,255,255,0.7)] backdrop-blur-sm">
          <div className="border-blue-dianne/60 flex items-center gap-3 border-b bg-[rgba(253,216,104,0.65)] px-3 py-1.5">
            <span className="font-display text-caption text-ink min-w-0 flex-1 truncate font-bold">
              {seasonOver
                ? "what it looked like..."
                : "what your event could look like..."}
            </span>
            {/* Decorative window chrome, not a real control. */}
            <span
              aria-hidden
              className="border-line text-ink font-display text-caption flex h-5 w-5 items-center justify-center border bg-[rgba(253,83,56,0.5)] leading-none"
            >
              x
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 p-3">
            {PHOTOS.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                aria-hidden
                loading="lazy"
                className="border-blue-dianne/50 h-40 w-full border object-cover sm:h-88"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
